import type React from "react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../lib/i18n";
import type { ReactNode } from "react";
import type { Tone } from "./Button";

/*
 * 文字用 800：原先直接拿基色当字（#e6a23c 放在浅黄底上约 2.2:1），标签里恰恰是「启用 / 停用 /
 * 未上线」这类运营要一眼读出的状态。底色不变，观感只是字更实。
 */
const SKIN: Record<Exclude<Tone, "default">, string> = {
  primary: "bg-primary-50 text-primary-800",
  success: "bg-success-50 text-success-800",
  warning: "bg-warning-50 text-warning-800",
  danger: "bg-danger-50 text-danger-800",
  info: "bg-info-50 text-info-800",
};

export function Tag({ tone = "info", children }: { tone?: Exclude<Tone, "default">; children: ReactNode }) {
  return <span className={`rounded-sm px-1.5 py-px text-xs font-semibold whitespace-nowrap ${SKIN[tone]}`}>{children}</span>;
}

/** 版本状态。三种状态的语义固定，不要让调用方自己传颜色——不然同一状态在不同页面会是两个色 */
export type VersionStatus = "draft" | "published" | "active";
const STATUS: Record<VersionStatus, string> = {
  draft: "bg-info-50 text-info-800 border-info-300",
  published: "bg-primary-50 text-primary-800 border-primary-300",
  active: "bg-success-50 text-success-800 border-success-300",
};

export function StatusTag({ status }: { status: VersionStatus }) {
  const t = useT();
  const cls = STATUS[status] ?? STATUS.draft;
  return (
    <span className={`rounded-sm border px-2 py-px text-xs font-semibold whitespace-nowrap ${cls}`}>
      {t(`status.${status}` as "status.draft")}
    </span>
  );
}

export function CheckIcon({ ok, size = 14 }: { ok: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className="flex-none">
      <circle cx="8" cy="8" r="7" className={ok ? "fill-success" : "fill-danger"} />
      {ok ? (
        <path d="M4.5 8.2l2.3 2.3 4.7-4.8" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  );
}

/**
 * 即时提示（替代原生 title）——对应设计稿 ui.jsx 的 Tip：原生 title 有系统级 ~1s 延迟，校验失败原因要一悬停就出来（leo 2026-08-25）。
 * hover 时按触发元素的位置用 fixed 定位、portal 到 body——不会被 DataTable 的 overflow-auto 或单元格的 overflow-hidden 裁掉。
 * 靠近视口底部时向上弹；贴右边缘时向左收。`lines` 多条分行；`dark` 深色底白字（校验类），默认浅色（字段说明）。
 */
export function Tip({ text, lines, dark, children, className }: {
  text?: string | null; lines?: string[]; dark?: boolean; children: React.ReactNode; className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; up: boolean } | null>(null);
  const items = lines ?? (text ? [text] : []);
  const show = () => {
    const el = ref.current;
    if (!el || !items.length) return;
    const r = el.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 140; // 底部不够放就往上弹
    setPos({ left: Math.min(Math.max(r.left, 12), window.innerWidth - 332), top: up ? r.top - 4 : r.bottom + 4, up });
  };
  const hide = () => setPos(null);
  return (
    <span ref={ref} className={"inline-flex items-center " + (className ?? "")} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {pos && items.length > 0 && createPortal(
        <span
          role="tooltip"
          style={{ position: "fixed", left: pos.left, top: pos.top, transform: pos.up ? "translateY(-100%)" : "none" }}
          className={
            "pointer-events-none z-[1000] w-max max-w-[320px] rounded-base px-2.5 py-2 text-left text-xs font-normal leading-relaxed shadow-md " +
            (dark ? "bg-ink text-white" : "border border-line-lighter bg-white text-ink-regular")
          }
        >
          {items.length > 1 ? items.map((l, k) => <div key={k} className={k ? "mt-1" : ""}>{l}</div>) : items[0]}
        </span>,
        document.body,
      )}
    </span>
  );
}
